import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  updateServiceSchema,
  type UpdateServiceDto,
  type UpdateServicePayload,
} from '@services/services/dtos/update.dto';
import { serviceService } from '@services/services/service';
import type { GetAllServicesOutput } from '@services/services/types/getAll.type';
import type { GetServiceByIdOutput } from '@services/services/types/getById.type';
import type { UpdateServiceOutput } from '@services/services/types/update.type';

import { ArchiveServiceAlert } from '../@components/ArchiveAlert';
import { RestoreServiceAlert } from '../@components/RestoreAlert';

function serviceQueryOptions(id: string) {
  return queryOptions({
    queryFn: () => serviceService.getById(id),
    queryKey: queryKeys.services.detail(id),
    staleTime: EStaleTime.FiveMinutes,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/business-lines/$id/services/$serviceId/update',
)({
  loader: ({ context, params }) => {
    const options = serviceQueryOptions(params.serviceId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  component: UpdateService,
});

function UpdateService() {
  const { id: businessLineId, serviceId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { queryClient } = Route.useRouteContext();

  const { data } = useSuspenseQuery(serviceQueryOptions(serviceId));

  const form = useForm<UpdateServiceDto>({
    defaultValues: {
      translations: {
        en: data.service.translations.en,
        fr: data.service.translations.fr,
        nl: data.service.translations.nl,
      },
    },
    mode: 'onBlur',
    resolver: zodResolver(updateServiceSchema),
  });

  const { isPending, mutate: updateService } = useMutation<
    UpdateServiceOutput,
    unknown,
    UpdateServicePayload
  >({
    mutationFn: serviceService.update,
    onError: handleRequestError,
    onSuccess: async ({ service: updated }) => {
      const detailQueryKey = queryKeys.services.detail(serviceId);
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      queryClient.setQueryData<GetServiceByIdOutput>(detailQueryKey, old => {
        if (!old) {
          return old;
        }
        return {
          service: { ...old.service, translations: updated.translations },
        };
      });
      queryClient.setQueriesData<GetAllServicesOutput>(
        { queryKey: queryKeys.services.listByBusinessLine(businessLineId) },
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            list: old.list.map(item =>
              item.id === serviceId
                ? { ...item, translations: updated.translations }
                : item,
            ),
          };
        },
      );
      navigate({
        replace: true,
        to: '/business-lines/$id',
        params: { id: businessLineId },
      });
      toast.success('Success', {
        description: 'Service updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateService({ id: serviceId, translations: values.translations });
  });

  return (
    <Form.Provider {...form}>
      <div className="flex items-center justify-between">
        <PageTitle
          hasBackButton
          params={{ id: businessLineId }}
          title="Update Service"
          to="/business-lines/$id"
        />
        {data.service.archived ? (
          <RestoreServiceAlert />
        ) : (
          <ArchiveServiceAlert />
        )}
      </div>

      <form
        className="mx-auto mt-5 space-y-3 p-2 md:w-4/5 2xl:w-3/5"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-2 sm:grid-cols-3">
          <InputControlled label="Translation (EN)" name="translations.en" />
          <InputControlled label="Translation (FR)" name="translations.fr" />
          <InputControlled label="Translation (NL)" name="translations.nl" />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{
              id: businessLineId,
            }}
            rounded="lg"
            size="lg"
            to="/business-lines/$id"
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
